import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRefetch } from '../composables/use-refetch';
import type { BffError } from '../types/types';

/* BFF Error Handling */
export const useErrorStore = defineStore('errors', () => {
    type ErrorObject = Record<BffError['location'], Pick<BffError, 'message' | 'recommendedUserAction' | 'key'>[]>
    const errorObject = ref<ErrorObject>({});

    function handleBffErrors(errors: BffError[]) {
        errors.forEach(({ location, message, recommendedUserAction, key }) => {
            if (!errorObject.value[location]) {
                errorObject.value[location] = [];
            }
            errorObject.value[location].push({ message, recommendedUserAction, key });
        });
    }

    function createLocationErrorObject(location) {
        return computed(() => ({
            messages: [...(new Set(errorObject.value[location]?.map(error => error.message)) ?? [])].filter(Boolean),
            recommendedActions: [...(new Set(errorObject.value[location]
                ?.map(error => error.recommendedUserAction)) ?? [])].filter(Boolean),
            keys: [...(new Set(errorObject.value[location]?.map(error => error.key)) ?? [])].filter(Boolean),
            clear() {
                errorObject.value[location] = [];
            },
        }));
    }

    // Computed property that counts errors with "reload" action
    const errorsWithReloadAsRecommendedAction = computed(() => Object.values(errorObject.value)
        .flat().filter(error => error.recommendedUserAction === 'reload').length);

    // Concept:
    watch(errorsWithReloadAsRecommendedAction, (newCount, oldCount) => {
        const { shouldRefetchEverythingOnReload } = useRefetch();
        if (newCount > oldCount) {
            shouldRefetchEverythingOnReload.value = true;
        }
    });

    const generalErrors = createLocationErrorObject('general');
    const cvErrors = createLocationErrorObject('cv');
    const mailErrors = createLocationErrorObject('mail');

    function $reset() {
        errorObject.value = {};
    }

    return {
        handleBffErrors,
        generalErrors,
        cvErrors,
        mailErrors,
        $reset,
    };
});
